import { initializeApp, type FirebaseOptions } from 'firebase/app';
import { getAuth, signInAnonymously } from 'firebase/auth';
import { getStorage } from 'firebase/storage';
import { getDatabase } from 'firebase/database';
import firebaseConfigJson from '../firebase-config.json';

// Keep runtime config in one place: firebase-config.json
const firebaseConfig: FirebaseOptions = {
  apiKey: firebaseConfigJson.apiKey,
  authDomain: firebaseConfigJson.authDomain,
  projectId: firebaseConfigJson.projectId,
  storageBucket: firebaseConfigJson.storageBucket,
  messagingSenderId: firebaseConfigJson.messagingSenderId,
  appId: firebaseConfigJson.appId,
  // Realtime Database URL (optional but recommended for region-specific instances)
  databaseURL: (firebaseConfigJson as { realtimeDatabaseInstanceUri?: string }).realtimeDatabaseInstanceUri,
};

// Initialize Firebase
export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const storage = getStorage(app);
export const db = getDatabase(app);

// Initialize anonymous authentication; return null when this provider is blocked.
export async function initAuth(): Promise<string | null> {
  if (auth.currentUser?.uid) {
    return auth.currentUser.uid;
  }

  try {
    const result = await signInAnonymously(auth);
    return result.user.uid;
  } catch (error) {
    const code = (error as { code?: string })?.code;
    if (code === 'auth/admin-restricted-operation' || code === 'auth/operation-not-allowed') {
      console.warn(
        'Anonymous auth is not available for this project. Continuing without Firebase auth.',
        error
      );
      return null;
    }

    console.error('Failed to sign in anonymously:', error);
    throw error;
  }
}

// Get current user ID
export function getCurrentUserId(): string | null {
  return auth.currentUser?.uid || null;
}
